import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { PurchaseOrderRecord } from '@/types/domain/po-management';
import { formatDate } from '@/lib/utils';
import { Truck, AlertTriangle } from 'lucide-react';

interface PoDeliveryTrackerProps {
  po: PurchaseOrderRecord;
}

export const PoDeliveryTracker: React.FC<PoDeliveryTrackerProps> = ({ po }) => {
  const totalOrdered = po.items.reduce((acc, i) => acc + i.quantityOrdered, 0);
  const totalDelivered = po.items.reduce((acc, i) => acc + i.quantityDelivered, 0);
  const totalPending = po.items.reduce((acc, i) => acc + i.quantityPending, 0);
  const deliveryPct = totalOrdered > 0 ? Math.round((totalDelivered / totalOrdered) * 100) : 0;

  return (
    <Card className="border-border rounded-2xl shadow-2xs font-heading">
      <CardHeader className="pb-3 border-b border-border/60">
        <CardTitle className="font-heading text-xs font-bold text-foreground uppercase tracking-wider flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Truck className="h-4 w-4 text-primary" />
            <span>Delivery Tracking & Fulfillment Milestone</span>
          </div>

          <span
            className={`text-[10px] font-extrabold uppercase px-2.5 py-0.5 rounded-full ${
              po.isDelayed
                ? 'bg-rose-500/10 text-rose-700 border border-rose-500/20'
                : po.status === 'DELIVERED'
                ? 'bg-emerald-500/10 text-emerald-700 border border-emerald-500/20'
                : 'bg-blue-500/10 text-blue-700 border border-blue-500/20'
            }`}
          >
            {po.isDelayed ? `Overdue by ${po.delayDays} Days` : po.status.replace(/_/g, ' ')}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {/* Overdue Exception Banner */}
        {po.isDelayed && (
          <div className="p-4 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-900 space-y-1">
            <div className="flex items-center gap-2 font-extrabold text-sm">
              <AlertTriangle className="h-5 w-5 text-rose-600 animate-bounce" />
              <span>DELIVERY OVERDUE EXCEPTION DETECTED</span>
            </div>
            <p className="text-xs text-rose-800 font-medium">
              Promised delivery date was <strong className="font-mono">{formatDate(po.promisedDeliveryDate)}</strong> ({po.delayDays} days ago). {totalPending} units remain pending for plant <strong className="font-semibold">{po.plant}</strong>.
            </p>
          </div>
        )}

        {/* Fulfillment Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 rounded-xl border border-border bg-muted/40 text-xs">
          <div className="space-y-0.5">
            <span className="text-[10px] font-extrabold text-foreground-muted uppercase block">Promised Delivery</span>
            <span className="font-mono font-bold text-foreground block">{formatDate(po.promisedDeliveryDate)}</span>
          </div>

          <div className="space-y-0.5">
            <span className="text-[10px] font-extrabold text-foreground-muted uppercase block">Total Ordered Qty</span>
            <span className="font-mono font-bold text-foreground block">{totalOrdered} Units</span>
          </div>

          <div className="space-y-0.5">
            <span className="text-[10px] font-extrabold text-foreground-muted uppercase block">Delivered GRN Qty</span>
            <span className="font-mono font-bold text-emerald-600 block">{totalDelivered} Units</span>
          </div>

          <div className="space-y-0.5">
            <span className="text-[10px] font-extrabold text-foreground-muted uppercase block">Pending Delivery Qty</span>
            <span className="font-mono font-bold text-amber-600 block">{totalPending} Units</span>
          </div>
        </div>

        {/* Delivery Progress Bar */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs font-bold text-foreground">
            <span>Overall Order Fulfillment</span>
            <span className="font-mono">{deliveryPct}% Completed</span>
          </div>
          <div className="w-full h-3 rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                po.isDelayed ? 'bg-amber-500' : deliveryPct === 100 ? 'bg-emerald-600' : 'bg-primary'
              }`}
              style={{ width: `${deliveryPct}%` }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
